// TalkTrackPanel — searchable + categorized in-call script panel.
//
// Closes audit response §9 Phase 3 SALES "no talk track library on
// screen during a call" and §11 Phase 5 RETENTION "chargeback recovery
// workflow is SMS-only, no scripted call playbook."
//
// Mounts inside the active CallWidget so the script lives WITH the
// call, not beside it. Reads from public.talk_tracks (migration 105)
// which seeds 25+ universal scripts and lets operators add per-tenant
// overrides via RLS-scoped writes.
//
// Props:
//   lead          — { name, leadType / vertical } so we can pre-filter
//                   by line_of_business
//   context       — 'outbound' | 'inbound' | 'callback' | 'voicemail'
//   phase         — 'loading' | 'ringing' | 'in-progress' | 'ended'
//                   so we can hide the panel when it's pointless
//   onCopy        — optional callback when a track is copied (logging,
//                   analytics)

import React from 'react';
import { supabase } from '../lib/supabase';

// Display-friendly category labels + sort order.
const CATEGORY_META = [
  { key: 'opening',       label: '👋 Opening',       tone: 'sky' },
  { key: 'qualification', label: '🔍 Qualify',       tone: 'violet' },
  { key: 'objection',     label: '🛡  Objections',   tone: 'amber' },
  { key: 'compliance',    label: '⚖  Compliance',    tone: 'emerald' },
  { key: 'closing',       label: '🤝 Closing',       tone: 'rose' },
  { key: 'retention',     label: '💾 Retention',     tone: 'red' },
  { key: 'cross_sell',    label: '➕ Cross-sell',    tone: 'emerald' },
  { key: 'voicemail',     label: '📞 Voicemail',     tone: 'slate' },
  { key: 'callback',      label: '↻ Callback',       tone: 'sky' },
  { key: 'inbound',       label: '↙ Inbound',        tone: 'sky' },
];

const TONE_CLASS = {
  sky:     'bg-sky-50 text-sky-900 border-sky-200',
  violet:  'bg-violet-50 text-violet-900 border-violet-200',
  amber:   'bg-amber-50 text-amber-900 border-amber-200',
  emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  rose:    'bg-rose-50 text-rose-900 border-rose-200',
  red:     'bg-red-50 text-red-900 border-red-200',
  slate:   'bg-slate-50 text-slate-900 border-slate-200',
};

// Lead vertical / leadType → talk_tracks.line_of_business value.
function inferLOB(lead) {
  const t = String(lead?.leadType || lead?.vertical || '').toLowerCase();
  if (/medicare|mapd|medigap/.test(t))     return 'medicare';
  if (/aca|marketplace|hcsc/.test(t))       return 'aca';
  if (/iul|index/.test(t))                  return 'iul';
  if (/final.{0,3}expense|\bfex\b/.test(t)) return 'final_expense';
  if (/mortgage/.test(t))                   return 'mortgage_protection';
  if (/dental/.test(t))                     return 'dental';
  if (/vision/.test(t))                     return 'vision';
  if (/supplemental|accident|critical|cancer|disability/.test(t)) return 'supplemental';
  if (/life|term|whole/.test(t))            return 'life';
  return null;
}

// Interpolate {{token}} placeholders. Best-effort — unknown tokens
// stay verbatim so the operator can spot + edit before reading aloud.
function interpolate(body, vars) {
  if (!body) return '';
  return String(body).replace(/\{\{(\w+)\}\}/g, (m, key) => {
    if (vars[key] != null && vars[key] !== '') return String(vars[key]);
    return m;
  });
}

export default function TalkTrackPanel({ lead, context = 'outbound', phase, onCopy, agentName, agentPhone, compact = false }) {
  const [allTracks, setAllTracks] = React.useState(null);
  const [selectedCategory, setSelectedCategory] = React.useState(
    context === 'voicemail' ? 'voicemail' :
    context === 'inbound'   ? 'inbound' :
    'opening'
  );
  const [search, setSearch] = React.useState('');
  const [copiedId, setCopiedId] = React.useState(null);
  const [expandedId, setExpandedId] = React.useState(null);

  // Load all tracks once (the table is small + the operator may navigate
  // categories rapidly). RLS scopes to shared + own.
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await supabase
          .from('talk_tracks')
          .select('id, owner_id, category, line_of_business, objection_kind, title, body, blurb, sort_order')
          .eq('is_active', true)
          .order('category')
          .order('sort_order')
          .limit(500);
        if (alive) setAllTracks(data || []);
      } catch {
        if (alive) setAllTracks([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  const lob = inferLOB(lead);

  // Filter pipeline: category → LOB-applicable → search.
  const visibleTracks = React.useMemo(() => {
    if (!allTracks) return [];
    const q = String(search || '').toLowerCase().trim();
    return allTracks.filter(t => {
      if (t.category !== selectedCategory) return false;
      // LOB filter — track applies if its lob is null (universal) OR
      // matches the lead's inferred LOB.
      if (t.line_of_business && lob && t.line_of_business !== lob) return false;
      if (q) {
        const hay = `${t.title} ${t.blurb || ''} ${t.body} ${t.objection_kind || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allTracks, selectedCategory, search, lob]);

  // Interpolation vars — pulled from the lead + agent profile.
  const interpVars = React.useMemo(() => ({
    first_name: lead?.firstName || String(lead?.name || '').split(' ')[0] || '',
    last_name:  lead?.lastName  || String(lead?.name || '').split(' ').slice(1).join(' ') || '',
    agent_name: agentName || 'your agent',
    agent_phone: agentPhone || '',
    agency_name: agentName || 'our agency',
    product:    lead?.product || lead?.leadType || 'coverage',
    carrier:    lead?.carrier || 'your carrier',
    premium:    lead?.premium || '',
    benefit:    lead?.benefit || 'protection',
    agent_license: '',
  }), [lead, agentName, agentPhone]);

  // Hide while the call is loading or ringing — script isn't useful yet.
  if (phase && (phase === 'loading' || phase === 'ringing')) return null;

  return (
    <div className={`bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col ${compact ? 'max-h-64' : 'max-h-[440px]'}`}>
      <div className="px-3 py-2 bg-slate-900 text-white flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">Talk tracks</span>
          {lob && (
            <span className="text-[10px] font-bold bg-violet-500/30 text-violet-100 border border-violet-400/40 rounded px-1.5 py-0.5">
              {lob.replace(/_/g, ' ')}
            </span>
          )}
        </div>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search scripts…"
          className="text-xs bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white placeholder-slate-400 w-full sm:w-40"
        />
      </div>

      {/* Category tabs */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-200 overflow-x-auto bg-slate-50">
        {CATEGORY_META.map(c => {
          const isActive = c.key === selectedCategory;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => { setSelectedCategory(c.key); setExpandedId(null); }}
              className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded whitespace-nowrap transition-colors ${
                isActive ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Track list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {allTracks == null && (
          <div className="text-xs text-slate-400 text-center py-4">Loading scripts…</div>
        )}
        {allTracks != null && visibleTracks.length === 0 && (
          <div className="text-xs text-slate-400 text-center py-4">
            No scripts match this filter. Try a different category or clear the search.
          </div>
        )}
        {visibleTracks.map(t => {
          const meta = CATEGORY_META.find(m => m.key === t.category);
          const tone = TONE_CLASS[meta?.tone || 'slate'];
          const isExpanded = expandedId === t.id;
          const interpolated = interpolate(t.body, interpVars);
          const handleCopy = async () => {
            try {
              await navigator.clipboard.writeText(interpolated);
              setCopiedId(t.id);
              setTimeout(() => setCopiedId(null), 1500);
              if (onCopy) onCopy(t);
            } catch {}
          };
          return (
            <div key={t.id} className={`border rounded-md ${tone} ${isExpanded ? 'shadow-sm' : ''}`}>
              <button
                type="button"
                onClick={() => setExpandedId(isExpanded ? null : t.id)}
                className="w-full text-left px-2 py-1.5 flex items-start justify-between gap-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-xs">{t.title}</div>
                  {t.blurb && !isExpanded && (
                    <div className="text-[10px] opacity-75 mt-0.5 truncate">{t.blurb}</div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {t.objection_kind && (
                    <span className="text-[9px] font-bold bg-white/60 px-1 py-0.5 rounded uppercase">
                      {t.objection_kind.replace(/_/g, ' ')}
                    </span>
                  )}
                  <span className="text-[10px] opacity-60">{isExpanded ? '▾' : '▸'}</span>
                </div>
              </button>
              {isExpanded && (
                <div className="px-2 pb-2 space-y-1.5">
                  <div className="bg-white/70 border border-white rounded p-2 text-xs leading-relaxed whitespace-pre-wrap">
                    {interpolated}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="text-[10px] font-bold uppercase bg-slate-900 hover:bg-black text-white px-2 py-1 rounded"
                    >
                      {copiedId === t.id ? '✓ Copied' : 'Copy'}
                    </button>
                    {t.owner_id == null && (
                      <span className="text-[10px] opacity-60">shared default</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
